import {defineStore} from "pinia";
import {useToast} from "vue-toastification";
import {useAppStore as appStore} from '@/store'

import dayjs from "dayjs";
import APIRequest from "@/libs/cloud_auth";

export const useRoomStore = defineStore("room", {
  state: () => ({
    isLoading: false,
    dragging: false,
    selectedDate: new dayjs(),
    selectedState: [],
    options: [],
    selectedRoom: [],
    updateRooms: false,
    rooms: [],
    rooms_new: [],
  }),
  getters: {
    getUpcomingEvents() {
      let eventsMap = new Map();

      for (let x = 0; x < this.rooms.length; x++) {
        for (let i = 0; i < this.rooms[x].meetings.length; i++) {
          let meeting = this.rooms[x].meetings[i];
          let startDate = dayjs(meeting.start_time);
          let endDate = dayjs(meeting.end_time);

          if (
            !dayjs().isAfter(endDate, "millisecond") &&
            dayjs().isBefore(startDate, "millisecond")
          ) {
            // Use meeting ID if available, otherwise combine name + start_time as a unique key
            let uniqueKey = meeting.id || `${meeting.name}_${meeting.start_time}`;

            if (eventsMap.has(uniqueKey)) {
              let existingEvent = eventsMap.get(uniqueKey);
              // Prevent duplicates just in case the same location is added twice
              if (!existingEvent.location_name.includes(meeting.location_name)) {
                existingEvent.location_name += `, ${meeting.location_name}`;
              }
            } else {
              // Spread syntax {...meeting} creates a shallow copy so we don't mutate state
              eventsMap.set(uniqueKey, { ...meeting });
            }
          }
        }
      }
      
      // Convert map back to an array and sort it properly by time
      return Array.from(eventsMap.values()).sort(
        (a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf()
      );
    },

    getOngoingEvents() {
      let eventsMap = new Map();
      
      for (let x = 0; x < this.rooms.length; x++) {
        for (let i = 0; i < this.rooms[x].meetings.length; i++) {
          let meeting = this.rooms[x].meetings[i];
          let startDate = dayjs(meeting.start_time);
          let endDate = dayjs(meeting.end_time);

          if (
            !dayjs().isAfter(endDate, "millisecond") &&
            dayjs().isBetween(startDate, endDate, "millisecond", "[]")
          ) {
            let uniqueKey = meeting.id || `${meeting.name}_${meeting.start_time}`;

            if (eventsMap.has(uniqueKey)) {
              let existingEvent = eventsMap.get(uniqueKey);
              if (!existingEvent.location_name.includes(meeting.location_name)) {
                existingEvent.location_name += `, ${meeting.location_name}`;
              }
            } else {
              eventsMap.set(uniqueKey, { ...meeting });
            }
          }
        }
      }
      
      return Array.from(eventsMap.values()).sort(
        (a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf()
      );
    },

    getRoomOptions() {
      let rooms = this.options.map((el) => { return { name: el.name, value: el.id } });
      return rooms;
    }
  },
  actions: {
    rgbToHex(color){
      if (color.startsWith("#")) {
        return color; // Input is already a hex value, return it as is
      }

      let values = color.match(/\d+/g); // Extract numeric values from the string
      let r = parseInt(values[0].trim());
      let g = parseInt(values[1].trim());
      let b = parseInt(values[2].trim());
      return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    },
    async getScheduler(allDates = false) {
      try {
        let url = `timeline/scheduler-list/?source_ref_id=${appStore().getCurrentTenant}`;
        if (!allDates) {
          url = `timeline/scheduler-list/?date_value=${dayjs(this.selectedDate).format("YYYY-MM-DD")}&source_ref_id=${appStore().getCurrentTenant}`;
        }
        let response = await APIRequest(url);
        
        // Normalize the backend discrepancy between wayfinder_visibility and is_wayfinder_visible
        if (response.data) {
          response.data.forEach(location => {
            if (location.meetings) {
              location.meetings.forEach(meeting => {
                meeting.is_wayfinder_visible = meeting.is_wayfinder_visible ?? meeting.wayfinder_visibility ?? true;
              });
            }
          });
        }
        
        this.rooms = response.data;
        return response.data;
      } catch (error) {
        useToast().error(error.message);
      }
    },
    async getLocations() {
      try {
        let response = await APIRequest(
          `gadgets/location/?type=conference&page=1&page_size=100000&ordering=name&tenant=${appStore().getCurrentTenant}`
        ).catch(e => console.error(e))
        if (response.status === 200) return response.data;
      } catch (error) {
        useToast().error(error.message);
      }
    },
    async getAllLocations() {
      try {
        let response = await APIRequest(
          `gadgets/device/?ordering=name&page=1&page_size=100000&location__tenant=${appStore().getCurrentTenant}`
        ).catch(e => console.error(e))
        if (response.status === 200) return response.data;
      } catch (error) {
        useToast().error(error.message);
      }
    },
    async addEvent(form) {
      const conference_meetings = form.room.map((roomId) => ({
        color_code: this.rgbToHex(form.color),
        location: roomId,
        logo_asset: form.logo,
      }));

      return new Promise((resolve, reject) => {
        APIRequest(`timeline/scheduler/`, 'post', {
            scheduled_contents: [],
            conference_meetings,
            name: form.title,
            description: form.description,
            is_wayfinder_visible: !form.wayfinder_visibility,
            type: "conference",
            source_ref_type: 'tenant',
            source_ref_id: appStore().getCurrentTenant,
            start_time: dayjs(form.range.start).format('YYYY-MM-DD HH:mm'),
            end_time:  dayjs(form.range.end).format('YYYY-MM-DD HH:mm'),
            is_recurrent: false,
            recurrency_pattern: null,
          })
          .then((response) => {
            // Resolve the Promise with the response data
            useToast().success("Event added successfully!");
            resolve(response.data);
          })
          .catch((error) => {
            // Reject the Promise with the error
            useToast().error("Error adding event!");
            reject(error);
          });
        });
      },
    async editEvent(form, room) {

      const conference_meetings = form.room.map((locationId) => {
        return {
          scheduler: room.id,
          location: locationId,
          color_code: this.rgbToHex(form.color),
          logo_asset: form.logo,
        };
      });

      {
        return new Promise((resolve, reject) => {
          APIRequest(`timeline/scheduler/${room.id}/`, 'patch', {
              id: room.id,
              scheduled_contents: [],
              conference_meetings,
              name: form.title,
              description: form.description,
              is_wayfinder_visible: !form.wayfinder_visibility,
              start_time: dayjs(form.range.start).format('YYYY-MM-DD HH:mm'),
              end_time:  dayjs(form.range.end).format('YYYY-MM-DD HH:mm'),
              is_recurrent: room.is_recurrent,
              recurrency_pattern: room.recurrency_pattern,
            })
            .then((response) => {
              // Resolve the Promise with the response data
              useToast().success("Event edited successfully!");
              resolve(response.data);
            })
            .catch((error) => {
              // Reject the Promise with the error
              useToast().error("Error editing event!");
              reject(error);
            });
        });
      }
    },
    async getEvent(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`timeline/scheduler/${id}/`, 'get')
          .then((response) => {
            // Resolve the Promise with the response data
            if (response.data) {
              response.data.is_wayfinder_visible = response.data.is_wayfinder_visible ?? response.data.wayfinder_visibility ?? true;
            }
            resolve(response.data);
          })
          .catch((error) => {
            // Reject the Promise with the error
            reject(error);
          });
      });
    },
    async duplicateEvent(id) {
      try {
        this.isLoading = true;
        const response = await APIRequest(`timeline/duplicate-conference/`, 'post', { id });

        if (!response || (response.status && response.status >= 400) || response.error) {
          throw response || new Error("Duplicate failed");
        }

        await this.getScheduler(); 
        this.updateRooms = true;
        this.isLoading = false;

        return response.data;
      } catch (error) {
        this.isLoading = false;
        throw error;
      }
    },
    async removeEvent(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`timeline/scheduler/${id}/`, 'delete')
          .then((response) => {
            // Resolve the Promise with the response data
            useToast().success("Event deleted");
            resolve(response.data);
          })
          .catch((error) => {
            // Reject the Promise with the error
            useToast().error("Error deleting event!");
            reject(error);
          });
      });
    },
    async dragEvent(roomId, event) {
      return new Promise((resolve, reject) => {
        APIRequest(`timeline/scheduler/${event.id}/conference-meeting/${event.conference_id}/`, 'put', {
                location: roomId,
              })
          .then((response) => {
            // Resolve the Promise with the response data           
            resolve(response.data);
          })
          .catch((error) => {
            // Reject the Promise with the error
            useToast().error("Error moving event!");
            reject(error);
          });
      });

    },
  },
});
